// MACHINERY V2 (Stroje) — THE machineId IDENTITY GUARD for a v2 surface that
// reads an app-wide slice (M2.5 review, finding R1).
//
// >>> REQUIRED PATTERN. Any v2 surface that renders an app-wide Redux slice
// >>> keyed by machine MUST route it through this hook (or through the same
// >>> id comparison inline, where the data itself carries the id). Reading such
// >>> a slice straight into JSX is the defect this file exists to stop. <<<
//
// WHY. `customerServiceReports.serviceReports`, `customerMachineDocuments
// .documents` and `customerMachine.installedStockItems` are APP-WIDE slots that
// nothing clears on mount — each fulfilled reducer simply assigns the newest
// payload over the previous machine's array (customerMachineServiceReports
// Slice.ts:117, customerMachineDocumentsSlice.ts:62, customerMachineSlice.ts:108
// — all three personally read in this worktree, ticket process rule (a)).
// So between navigating machine A -> B and B's own response landing, the slot
// still holds A's array: A's rows, A's chip counts and A's `hasAny` render under
// B's hero, and indefinitely if B's fetch fails. Those rows carry DESTRUCTIVE
// and download actions, so B's profile could delete A's document or unlink A's
// part. `loading && !data` does NOT catch it — `data` is non-empty, it is just
// the wrong machine's.
//
// THE FIX IS THE ONE ALREADY IN THIS TREE, TWICE, AND IT IS DELIBERATELY NOT A
// SLICE RESET. MachineProfileV2.tsx:137-142 renders `customerMachinery.machine`
// only while `machine.id === id`, and SpecificationsTabV2.tsx:86 renders
// `selectedModel` only while `selectedModel.id === catalogModelId`. Neither adds
// a reset reducer, because those slices are shared with the LIVE v1 pages and
// new reducer surface would reach them. Same rule here. This hook exists
// because the three LISTS cannot be compared item-by-item the way those two
// single objects can: a Document carries no machineId at all
// (models/machineDocumentsModel.tsx:9-21), so ownership of the shared slot is
// recorded per settled request instead.
//
// ATTRIBUTION IS TO THE REQUEST, NEVER TO THE SCREEN. `track` is told which
// machineId the request was FOR, so a response that lands after the user has
// moved on claims — or fails — the machine it actually belongs to. The worst
// case of a late stale fulfilment is therefore that the tab shows its SKELETON
// (the slot is provably not ours) until the next fetch, never another machine's
// rows. A blank-but-wrong panel is recoverable; a destructive action against an
// invisible machine is not.
import { useCallback, useState } from "react";

export interface MachineScope {
  /** TRUE only while the shared slot provably holds THIS machineId's response. */
  owns: boolean;
  /** TRUE only when THIS machineId's own fetch rejected. */
  failed: boolean;
  /**
   * Wraps one in-flight fetch. `target` is the machineId the request was built
   * for — pass it explicitly rather than reading the current prop, so a late
   * fulfilment is attributed correctly. Never rejects: the outcome is state,
   * not an exception, so callers cannot leak an unhandled rejection.
   */
  track: (target: string, request: Promise<unknown>) => Promise<void>;
}

export const useMachineScope = (machineId: string): MachineScope => {
  const [owner, setOwner] = useState<string | null>(null);
  const [failedFor, setFailedFor] = useState<string | null>(null);

  const track = useCallback(
    (target: string, request: Promise<unknown>): Promise<void> =>
      request.then(
        () => {
          setFailedFor((prev) => (prev === target ? null : prev));
          setOwner(target);
        },
        () => {
          setFailedFor(target);
        }
      ),
    []
  );

  return { owns: owner === machineId, failed: failedFor === machineId, track };
};

export default useMachineScope;
